/**
 * Iter XX.222 — inline chat confirmation card.
 *
 * Rendered as a bubble kind after the coaching reply lands. Card copy
 * is intentionally minimal:
 *
 *   Save this innings?
 *   43 (37) · Today · caught at long-on
 *   [ Save ]   [ Not this one ]
 *
 * All-rounder → "Save this match?" with both batting + bowling lines.
 * Historical → no temporal chip; save just says "Add to My Cricket".
 *
 * Never appears mid-coaching. Never captures data on its own.
 */
import { useState } from "react";
import { Loader2, Check, X as XIcon } from "lucide-react";
import { confirmPerformance, dismissPerformance } from "../lib/performanceCapture";

function _fmtBattingLine(b) {
  if (!b) return null;
  const balls = b.balls != null ? ` (${b.balls})` : "";
  const no = b.not_out ? "*" : "";
  return `${b.runs}${no}${balls}`;
}

function _fmtBowlingLine(w) {
  if (!w) return null;
  const overs = w.overs != null ? `${w.overs} ov · ` : "";
  const runs = w.runs_conceded != null ? w.runs_conceded : "?";
  const wkts = w.wickets != null ? w.wickets : "?";
  return `${overs}${wkts}/${runs}`;
}

function _fmtDateChip(cand) {
  if (cand.historical) return null;      // no chip for older matches
  const hint = (cand.date_hint || "").toLowerCase();
  if (hint === "today") return "Today";
  if (hint === "yesterday") return "Yesterday";
  if (cand.date_iso) return cand.date_iso;
  return null;
}

export default function PerformanceConfirmCard({ candidate, onResolved }) {
  const [state, setState] = useState("idle"); // idle | saving | done | dismissed | error
  const [err, setErr] = useState("");

  const bat = candidate.batting;
  const bowl = candidate.bowling;
  const isAllRounder = bat && bowl;
  const battingLine = _fmtBattingLine(bat);
  const bowlingLine = _fmtBowlingLine(bowl);
  const dateChip = _fmtDateChip(candidate);
  const dismissal = bat?.dismissal_text;

  const handleSave = async () => {
    setState("saving"); setErr("");
    try {
      await confirmPerformance(candidate.candidate_id);
      setState("done");
      onResolved?.("confirmed");
    } catch (e) {
      setErr(String(e?.message || e)); setState("error");
    }
  };
  const handleDismiss = async () => {
    setState("saving");
    try {
      await dismissPerformance(candidate.candidate_id);
      setState("dismissed");
      onResolved?.("dismissed");
    } catch (e) {
      setErr(String(e?.message || e)); setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="rounded-xl border border-emerald-900/50 bg-emerald-950/30 px-3 py-2 my-2 flex items-center gap-2"
            data-testid="performance-card-saved">
        <Check size={14} className="text-emerald-400" />
        <span className="text-xs text-emerald-300">Saved to My Cricket</span>
      </div>
    );
  }
  if (state === "dismissed") return null;

  return (
    <div
      className="rounded-xl border border-neutral-800 bg-neutral-950/70 px-3 py-3 my-2"
      data-testid="performance-card"
      data-candidate-id={candidate.candidate_id}
    >
      <div className="text-xs font-medium text-neutral-400 mb-1"
            data-testid="performance-card-title">
        {isAllRounder ? "Save this match?"
          : bowl ? "Save this spell?"
          : "Save this innings?"}
      </div>
      <div className="text-sm text-neutral-100 leading-tight flex flex-wrap gap-x-2 gap-y-0.5"
            data-testid="performance-card-summary">
        {battingLine && <span>{battingLine}</span>}
        {isAllRounder && bowlingLine && <span className="text-neutral-500">·</span>}
        {bowlingLine && <span>{bowlingLine}</span>}
        {dateChip && <>
          <span className="text-neutral-500">·</span>
          <span className="text-neutral-400">{dateChip}</span>
        </>}
        {dismissal && !isAllRounder && <>
          <span className="text-neutral-500">·</span>
          <span className="text-neutral-400 lowercase">{dismissal}</span>
        </>}
      </div>
      {err && <p className="text-xs text-red-400 mt-1">{err}</p>}
      <div className="mt-2 flex gap-2">
        <button
          onClick={handleSave} disabled={state === "saving"}
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
          data-testid="performance-card-save"
        >
          {state === "saving" ? <Loader2 size={12} className="animate-spin" />
            : <Check size={12} />}
          Save
        </button>
        <button
          onClick={handleDismiss} disabled={state === "saving"}
          className="text-neutral-400 hover:text-neutral-200 text-xs px-2 py-1.5 inline-flex items-center gap-1"
          data-testid="performance-card-dismiss"
        >
          <XIcon size={12} />
          Not this one
        </button>
      </div>
    </div>
  );
}
